'use client';

import { useCallback, useEffect } from 'react';
import { useUserPreference } from '@/hooks/useUserPreferences';
import { A11Y_MODE_PREF_KEY, applyA11yMode, resolveA11yMode, type A11yMode } from '@/hooks/a11yMode';

/**
 * The user's accessible-mode setting. Persisted per user via useUserPreference;
 * a `?a11y=1|0` URL flag wins for this page load and is never written back.
 * Keeps <html data-a11y-mode> in step with the value (the boot script set the
 * first paint; this follows later changes and the server value arriving).
 */
export function useA11yMode(): [A11yMode, (mode: A11yMode) => void, { urlOverride: boolean }] {
  const [stored, setStored] = useUserPreference<string>(A11Y_MODE_PREF_KEY, 'off');
  const search = typeof window === 'undefined' ? '' : window.location.search;
  const urlOverride = /(?:^|[?&])a11y=[01](?:&|$)/.test(search);
  const mode = resolveA11yMode({ search, stored });

  useEffect(() => {
    applyA11yMode(document, mode);
  }, [mode]);

  const setMode = useCallback(
    (next: A11yMode) => {
      setStored(next);
      if (!urlOverride) applyA11yMode(document, next);
    },
    [setStored, urlOverride],
  );
  return [mode, setMode, { urlOverride }];
}
